import './limited-time.css';

import EventEmitter from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { OnlineOrderingService } from '../../../services/online-ordering-service';
import { Util } from '../../../utils/util';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IDataHolidayOrderType } from '../../../models/data.interface';
import { DialogCreators } from '../../../utils/dialog-creators';
import { ILimitedTime } from './limited-time.interface';
import { IGetLimitedTimes, IRemoveOrderLimitedTimes, IReserveLimitedTimes, IResetLimitedTimeTimer } from '../../../services/online-ordering-service.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { OrderType } from '../../../types/enums';

export class LimitedTime {
    private readonly _item: ILimitedTime;
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _onlineOrderingService: OnlineOrderingService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _getOrderTypeId: () => OrderType;

    /**
     * Initializes a new instance of the LimitedTime class.
     * @param eventBus - Event emitter for event handling.
     * @param dataService - Data service providing store information.
     * @param orderService - Order service managing order properties.
     * @param onlineOrderingService - Service for fetching online order data.
     * @param parentId - ID of the parent element for rendering.
     * @param getOrderTypeId - Optional override to source the order type from somewhere other than the order model (e.g. a pending, not-yet-applied selection). Defaults to reading from `order`.
     */
    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        order: Order,
        onlineOrderingService: OnlineOrderingService,
        dataLayerService: DataLayerService, parentId: string,
        getOrderTypeId?: () => OrderType
    ) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._onlineOrderingService = onlineOrderingService;
        this._dataLayerService = dataLayerService;
        this._getOrderTypeId = getOrderTypeId ?? (() => this._order.getProperty("OrderTypeID"));
        this._item = this._createInitialModel(parentId);
    }

    /**
     * Resolves the order type subtype for the currently effective order type (see _getOrderTypeId).
     * Returns the order's own cached subtype when it matches the real order type, to avoid any
     * behavior change for callers that don't override the order type.
     */
    private _getOrderTypeSubType(): any {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("OrderTypeSubType");

        return OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getProperty("OrderTypeSubTypes") ?? []) ?? this._order.getProperty("OrderTypeSubType");
    }

    /**
     * Resolves the wait time for the currently effective order type (see _getOrderTypeId).
     * Returns the order's own cached wait time when it matches the real order type, to avoid any
     * behavior change for callers that don't override the order type.
     */
    private _getWaitTime(): number {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("WaitTime");

        return OnlineOrderingUtil.getWaitTimeByOrderType(this._data, this._getOrderTypeSubType());
    }

    /**
     * Creates the initial limited-time model.
     * @param parentId - ID of the parent container.
     * @returns The initialized ILimitedTime object.
     */
    private _createInitialModel(parentId: string): ILimitedTime {
        return {
            parentId: parentId,
            date: new Date(),
            orderCount: [],
            maxCount: 0,
            timer: {
                timeCountdown: 0,
                intervalId: null,
                dialogId: null
            }
        };
    }

    /**
     * Handles the date change event asynchronously.
     * @param date - The new date to initialize with.
     */
    public handleDateChangeAsync = async (date: Date): Promise<void> => {
        await this.initAsync(date);
    }

    /**
     * Initializes the limited-time data with the provided date.
     * @param date - The date to initialize with.
     */
    public async initAsync(date: Date): Promise<void> {
        if (!this._data.storeKey)
            return;

        this._item.date = date;
        this.clearTimer();
        await this._fetchLimitedTimesDataAsync();
        this._renderTimeSlots();
    }

    /**
     * Fetches limited-time data from the online ordering service.
     */
    private async _fetchLimitedTimesDataAsync(): Promise<void> {
        const payload: IGetLimitedTimes = {
            SKY: this._data.getProperty("StoreInfo").storeKey,
            BDATE: Util.formatDateInput(this._item.date),
            OTID: this._getOrderTypeId(),
            GUID: this._order.getProperty("GUID")
        };

        const result = await this._onlineOrderingService.getLimitedTimes(payload);
        if (result.success)
            this._processOrderCounts(result.data);
    }

    /**
     * Renders time slots based on store hours and throttle limits.
     */
    private _renderTimeSlots(): void {
        const selectedDate = Util.GetDate_StartTime_EndTime(
            this._data, this._item.date, 0, false, this._getOrderTypeSubType()
        );

        const waitTime = this._data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : this._getWaitTime();

        let startTime = selectedDate.StartOrderMinute - waitTime;
        startTime = startTime + (15 - startTime % 15) % 15; // Round up to the next 15 minute increment

        const endTime = selectedDate.EndOrderMinute - (selectedDate.EndOrderMinute % 15);
        let startSkip = ((startTime % 60) > 0) ? (startTime % 60) / 15 : 0;
        const rows = Math.ceil((endTime - (startTime - startSkip * 15)) / 60);

        let throttleInfo: IDataHolidayOrderType | null = null;
        if (selectedDate.ThrottleInfo) {
            throttleInfo = selectedDate.ThrottleInfo.find(
                x => x.checked && x.orderTypeId == this._getOrderTypeId()
            ) || null;

            if (throttleInfo)
                this._item.maxCount = throttleInfo.orderAmount;
        }

        const parentDiv = document.getElementById(this._item.parentId);
        if (parentDiv) {
            parentDiv.innerHTML = "";
            for (let i = 0; i < rows; i++) {
                parentDiv.appendChild(this._createTimeRow(startTime, endTime, startSkip, throttleInfo));
                startTime += 60 - (startSkip * 15);
                startSkip = 0;
            }
            this._updateRovingTabindex(parentDiv);
            if (!parentDiv.dataset.arrowKeyListener) {
                parentDiv.dataset.arrowKeyListener = "1";
                parentDiv.addEventListener("keydown", this._onGroupKeyDown);
            }
        }
    }

    /**
     * Processes and normalizes the order count data.
     * @param data - Array of objects containing order count data.
     */
    private _processOrderCounts(data: { min: number, cnt: number }[]): void {
        this._item.orderCount = data.map(item => ({
            minute: item.min >= 240 ? item.min : item.min + 1440,
            count: item.cnt
        }));
    }

    /**
     * Handles time slot selection and reservation.
     * @param minutes - The selected time in minutes.
     * @param startTime - The starting time for the reservation.
     * @param maxSpan - The maximum duration for the reservation.
     */
    private readonly _handleTimeSelection = async (minutes: number, startTime: number, maxSpan: number): Promise<void> => {
        const payload: IReserveLimitedTimes = {
            GUID: this._order.getProperty("GUID"),
            SKY: this._data.getProperty("StoreInfo").storeKey,
            BDATE: Util.formatDateInput(this._item.date),
            OTID: this._getOrderTypeId(),
            MAX: this._item.maxCount,
            MIN: minutes,
            SMIN: startTime,
            EMIN: startTime + maxSpan
        };
        const result = await this._onlineOrderingService.reserveLimitedTimes(payload);
        if (!result.success)
            return;

        if (result.data == null) {
            this._order.setFutureDate(new Date(this._item.date), minutes);
            this._startExpirationTimer();
            this._renderTimeSlots();
            // Return focus to the selected slot after the re-render recreates all buttons.
            const parentDiv = document.getElementById(this._item.parentId);
            const selectedBtn = parentDiv?.querySelector(`#btn_limited_time_slot_${minutes}`) as HTMLElement | null;
            selectedBtn?.focus();
        } else {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayerService,
                this._localizationService.names(
                    "SelectedTimeNotAvailableSelectNew"
                ),
                true
            );
            this._processOrderCounts(result.data);
            this._renderTimeSlots();
        }
    }

    /**
     * Creates a row of available time slots.
     * @param rowStartMinute - Start minute for the row.
     * @param storeEndMinute - Store's closing time in minutes.
     * @param startSkipAmount - Number of slots to skip.
     * @param throttleInfo - Throttle limit information.
     * @returns The row of time slots as an HTMLElement.
     */
    private _createTimeRow(rowStartMinute: number, storeEndMinute: number, startSkipAmount: number, throttleInfo: IDataHolidayOrderType | null): HTMLElement {
        if (startSkipAmount > 0)
            rowStartMinute -= (startSkipAmount * 15);

        const groupAmount = throttleInfo ? throttleInfo.minutes : 15;
        const disableList = throttleInfo ? this._getDisabledTimeSlotsForThrottle(rowStartMinute, throttleInfo) : [];

        const rowElement = document.createElement("div");
        for (let i = 0; i < 4; i++) {
            rowElement.appendChild(this._createTimeSlotElement(i, rowStartMinute, storeEndMinute, startSkipAmount, throttleInfo, disableList, groupAmount));
        }

        return rowElement;
    }

    /**
     * Creates a single time slot element.
     * @param index - Index of the time slot in the row.
     * @param rowStartMinute - Starting minute for the row.
     * @param storeEndMinute - Store's closing time in minutes.
     * @param startSkipAmount - Number of slots to skip.
     * @param throttleInfo - Throttle limit information.
     * @param disableList - List of disabled time slots.
     * @param groupAmount - The grouping size for time slots.
     * @returns The time slot as an HTMLElement.
     */
    private _createTimeSlotElement(index: number, rowStartMinute: number, storeEndMinute: number, startSkipAmount: number, throttleInfo: IDataHolidayOrderType | null, disableList: number[], groupAmount: number): HTMLElement {
        const minutes = rowStartMinute + (15 * index);
        const time = this._getDateFromMinutes(minutes, this._data.getProperty("TimeOffset"));
        const hide = (minutes > storeEndMinute) || (startSkipAmount != 0 && index + 1 <= startSkipAmount);
        const disabled = disableList.includes(minutes);
        const isSelected = this._order.getProperty("FutureMinute") === minutes;

        const buttonTemplate = (disabled && !hide)
            ? this._buildDisabledSlotTemplate(time)
            : this._buildSelectableSlotTemplate(minutes, time, hide, isSelected);

        const element = Util.createHtmlElementFromTemplate(buttonTemplate);

        if (!disabled && element) {
            element.onclick = async () => {
                const startMinute = (minutes % groupAmount === 0) ? minutes : (minutes - (minutes % groupAmount));
                const throttleMinutes = throttleInfo?.minutes ? throttleInfo.minutes - 15 : 0;
                await this._handleTimeSelection(minutes, startMinute, throttleMinutes);
            }
        }

        return element;
    }

    private _buildDisabledSlotTemplate(time: Date): string {
        return `<div><span>${Util.formatTime(time)}</span></div>`;
    }

    private _buildSelectableSlotTemplate(minutes: number, time: Date, hide: boolean, isSelected: boolean): string {
        const classes = [
            hide ? "hidden" : "",
            isSelected ? "time-selected" : ""
        ].filter(Boolean).join(" ");

        return `<div><button id="btn_limited_time_slot_${minutes}" class="${classes}" role="radio" aria-checked="${isSelected}" tabindex="-1">${Util.formatTime(time)}</button></div>`;
    }

    private _updateRovingTabindex(container: HTMLElement): void {
        const radios = Array.from(container.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(.hidden)'));
        radios.forEach(r => { r.tabIndex = -1; });
        const checked = radios.find(r => r.getAttribute("aria-checked") === "true");
        const target = checked ?? radios[0];
        if (target) target.tabIndex = 0;
    }

    private readonly _onGroupKeyDown = (e: KeyboardEvent): void => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
        const container = e.currentTarget as HTMLElement;
        const radios = Array.from(container.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(.hidden)'));
        const idx = radios.indexOf(e.target as HTMLButtonElement);
        if (idx === -1) return;
        e.preventDefault();
        const next = (e.key === "ArrowRight" || e.key === "ArrowDown")
            ? radios[Math.min(idx + 1, radios.length - 1)]
            : radios[Math.max(idx - 1, 0)];
        radios.forEach(r => { r.tabIndex = -1; });
        next.tabIndex = 0;
        next.focus();
    }

    /**
    * Returns a Date object set to the given minutes past midnight.
    *
    * @param {number} minutes - The number of minutes past midnight.
    * @return {Date} - The Date object set to the specified time.
    */
    private _getDateFromMinutes(minutes: number, timeOffset: number): Date {
        const date = Util.NowStore(timeOffset);
        date.setHours(0, 0, 0, 0);
        date.setMinutes(minutes);

        return date;
    }

    /**
     * Determines which time slots should be disabled based on throttle limits.
     * @param groupStartMinute - The starting minute of the group.
     * @param throttleInfo - Information about throttle restrictions.
     * @returns A list of disabled time slots.
     */
    private _getDisabledTimeSlotsForThrottle(groupStartMinute: number, throttleInfo: IDataHolidayOrderType): number[] {
        const interval = throttleInfo.minutes;
        const iterations = 60 / interval;

        return Array.from({ length: iterations }, (_, i) =>
            this._getDisabledTimeSlots(groupStartMinute + (interval * i), throttleInfo)
        ).flat();
    }

    /**
     * Determines which time slots should be disabled based on throttle limits.
     * @param groupStart - The starting time of the group.
     * @param throttleInfo - The throttle settings for the time slots.
     * @returns A list of disabled time slots.
     */
    private _getDisabledTimeSlots(groupStart: number, throttleInfo: IDataHolidayOrderType): number[] {
        const disableList: number[] = [];
        let totalOrderCount = 0;

        for (let j = 0; j < throttleInfo.minutes; j += 15) {
            totalOrderCount += this._item.orderCount.find(x => x.minute === groupStart + j)?.count || 0;
        }

        if (totalOrderCount >= throttleInfo.orderAmount) {
            for (let j = 0; j < throttleInfo.minutes; j += 15) {
                disableList.push(groupStart + j);
            }
        }

        return disableList;
    }

    /**
     * Starts a timer to track expiration of the selected time slot.
     */
    private _startExpirationTimer(): void {
        this.clearTimer();
        this._item.timer.timeCountdown = this._data.getProperty("Settings").OOS.throttlingTimeWindow;

        this._item.timer.intervalId = setInterval(() => {
            this._item.timer.timeCountdown -= 1;

            if (this._item.timer.timeCountdown === 1) {
                this._item.timer.dialogId = DialogCreators.messageBox(this._localizationService.names("TimeFrameToExpireContinue"), [
                    { "text": this._localizationService.names("No"), "callBack": () => { this._handleTimerExpiration(); } },
                    { "text": this._localizationService.names("Yes"), "callBack": () => { this._resetExpirationTimer(); } }
                ]);
            }

            if (this._item.timer.timeCountdown <= 0)
                this._handleTimerExpiration();
        }, 60 * 1000);
    }

    /**
     * Resets the expiration timer for the selected time slot.
     */
    private readonly _resetExpirationTimer = async (): Promise<void> => {
        if (!this._item.timer.intervalId)
            return;

        const timeCountdown = this._data.getProperty("Settings").OOS.throttlingTimeWindow;

        const payload: IResetLimitedTimeTimer = {
            SKY: this._data.getProperty("StoreInfo").storeKey,
            GUID: this._order.getProperty("GUID"),
            TIME: timeCountdown
        };

        const result = await this._onlineOrderingService.resetLimitedTimeTimer(payload);
        if (result)
            this._item.timer.timeCountdown = timeCountdown;
        else
            this._handleTimerExpiration();
    }

    /**
     * Handles expiration of the selected time slot.
     */
    private readonly _handleTimerExpiration = async (): Promise<void> => {
        if (!this._item.timer.intervalId)
            return;

        const payload: IRemoveOrderLimitedTimes = {
            SKY: this._data.getProperty("StoreInfo").storeKey,
            GUID: this._order.getProperty("GUID")
        };

        const result = await this._onlineOrderingService.removeOrderLimitedTimes(payload);
        if (!result)
            return;

        if (this._item.timer.dialogId)
            Util.DialogFadeOut(this._item.timer.dialogId);

        const orderTypeName = OnlineOrderingUtil.GetOrderTypeName(
            this._getOrderTypeId(),
            this._data.getProperty("OrderTypeSubTypes"),
            this._localizationService);
        const minutes = this._getOrderTypeSubType().WaitTime.toString();

        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayerService,
            this._localizationService
                .names("Error_ReservedTimeSlotExpired")
                .replace("{{order_type_name}}", orderTypeName)
                .replace("{{minutes}}", minutes),
            true
        );
        this._eventBus.emit(EventTypes.LIMITED_TIME_EXPIRED);
    }

    /**
     * Clears the expiration timer.
     */
    public clearTimer(): void {
        if (!this._item.timer.intervalId)
            return;

        clearInterval(this._item.timer.intervalId);
        this._item.timer = {
            timeCountdown: 0,
            intervalId: null,
            dialogId: null
        };
    }
}